(() => {
  let isLoading = false;
  let productGrid = null;
  let productGridContainer = null;
  let nextHref = null;
  let isInitialized = false;
  let mutationObserver = null;
  let refreshTimeout = null;
  let lastSignature = '';
  const seenProductUrls = new Set();

  function log(...args) {
    console.log('[Infinite Scroll]', ...args);
  }

  function normalizeUrl(url) {
    try {
      return new URL(url, window.location.origin).toString();
    } catch (e) {
      return url;
    }
  }

  function getProductUrlFromItem(item) {
    const link = item.querySelector('a[href*="/products/"]');
    return link ? normalizeUrl(link.href) : null;
  }

  function collectExistingProducts() {
    seenProductUrls.clear();

    const grid = document.querySelector('#product-grid');
    if (!grid) return;

    Array.from(grid.children).forEach((item) => {
      const url = getProductUrlFromItem(item);
      if (url) seenProductUrls.add(url);
    });

    log('Collected existing product URLs:', seenProductUrls.size);
  }

  function findNextHref(root = document) {
    const selectors = [
      '#infinite-scroll-next',
      'a[rel="next"]',
      '.pagination__item--next a',
      '.pagination .pagination__item[aria-label*="Next"]',
      '.pagination .pagination__item[aria-label*="next"]',
      '.pagination a.next',
      '.pagination a[aria-label="Next page"]'
    ];

    for (const selector of selectors) {
      const el = root.querySelector(selector);
      if (el && el.getAttribute('href')) {
        return el.getAttribute('href');
      }
    }

    const links = root.querySelectorAll('.pagination a, nav a, a');
    for (const link of links) {
      const text = (link.textContent || '').trim().toLowerCase();
      const aria = (link.getAttribute('aria-label') || '').trim().toLowerCase();
      const href = link.getAttribute('href');

      if (
        href &&
        (text === 'next' ||
          text === '→' ||
          text === '›' ||
          text === 'next page' ||
          aria.includes('next'))
      ) {
        return href;
      }
    }

    return null;
  }

  function ensureHiddenNextLink(href) {
    let hiddenLink = document.querySelector('#infinite-scroll-next');

    if (!href) {
      if (hiddenLink) hiddenLink.remove();
      return;
    }

    if (!hiddenLink) {
      hiddenLink = document.createElement('a');
      hiddenLink.id = 'infinite-scroll-next';
      hiddenLink.style.display = 'none';
      document.body.appendChild(hiddenLink);
    }

    hiddenLink.setAttribute('href', href);
  }

  function getElements() {
    productGridContainer = document.querySelector('#ProductGridContainer');
    productGrid = document.querySelector('#product-grid');
    nextHref = findNextHref(document);

    ensureHiddenNextLink(nextHref);

    return {
      hasContainer: !!productGridContainer,
      hasGrid: !!productGrid,
      hasNextHref: !!nextHref,
      href: nextHref
    };
  }

  function getSignature() {
    const container = document.querySelector('#ProductGridContainer');
    const grid = document.querySelector('#product-grid');
    const detectedNextHref = findNextHref(document);

    return JSON.stringify({
      hasContainer: !!container,
      hasGrid: !!grid,
      productCount: grid ? grid.children.length : 0,
      nextHref: detectedNextHref,
      url: window.location.href
    });
  }

  async function loadNextPage() {
    if (isLoading || !nextHref || !productGrid) return;

    const href = normalizeUrl(nextHref);
    isLoading = true;

    productGridContainer?.classList.add('infinite-scroll-loading');
    document.documentElement.classList.add('infinite-scroll-enabled');

    log('Loading next page:', href);

    try {
      const response = await fetch(href, {
        credentials: 'same-origin',
        cache: 'no-store'
      });

      if (!response.ok) {
        throw new Error(`Failed to load next page: ${response.status}`);
      }

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const newGrid = doc.querySelector('#product-grid');
      if (!newGrid) {
        throw new Error('No #product-grid found in fetched page');
      }

      const newItems = Array.from(newGrid.querySelectorAll(':scope > li'));
      log('New items found:', newItems.length);

      let appendedCount = 0;

      newItems.forEach((item) => {
        const productUrl = getProductUrlFromItem(item);

        if (!productUrl || !seenProductUrls.has(productUrl)) {
          if (productUrl) seenProductUrls.add(productUrl);
          productGrid.appendChild(item);
          appendedCount += 1;
        }
      });

      log('Actually appended new items:', appendedCount);

      const fetchedNextHref = findNextHref(doc);
      const normalizedFetchedNextHref = fetchedNextHref ? normalizeUrl(fetchedNextHref) : null;

      if (
        normalizedFetchedNextHref &&
        normalizedFetchedNextHref !== href &&
        normalizedFetchedNextHref !== normalizeUrl(nextHref)
      ) {
        nextHref = normalizedFetchedNextHref;
        ensureHiddenNextLink(normalizedFetchedNextHref);
        log('Updated next page href:', normalizedFetchedNextHref);
      } else {
        nextHref = null;
        ensureHiddenNextLink(null);
        log('No more pages, duplicate next href, or next href did not advance');
      }

      if (appendedCount === 0) {
        nextHref = null;
        ensureHiddenNextLink(null);
        log('No new products appended, stopping to avoid duplicates');
      }

      lastSignature = getSignature();

      setTimeout(() => {
        setupInfiniteScroll(true);
      }, 50);
    } catch (error) {
      console.error('[Infinite Scroll]', error);
    } finally {
      isLoading = false;
      productGridContainer?.classList.remove('infinite-scroll-loading');
    }
  }

  function maybeLoadMore() {
    if (isLoading || !nextHref || !productGrid) return;

    const scrollPosition = window.innerHeight + window.scrollY;
    const triggerPoint = document.documentElement.scrollHeight - 1200;

    if (scrollPosition >= triggerPoint) {
      loadNextPage();
    }
  }

  function setupInfiniteScroll(force = false) {
    const state = getElements();
    const newSignature = getSignature();

    log('Setup:', state, 'force:', force);

    if (
      !force &&
      newSignature === lastSignature &&
      state.hasContainer &&
      state.hasGrid &&
      state.hasNextHref
    ) {
      return;
    }

    lastSignature = newSignature;

    if (!state.hasContainer || !state.hasGrid) {
      document.documentElement.classList.remove('infinite-scroll-enabled');
      return;
    }

    collectExistingProducts();

    if (state.hasNextHref) {
      document.documentElement.classList.add('infinite-scroll-enabled');
    } else {
      document.documentElement.classList.remove('infinite-scroll-enabled');
    }

    if (!isInitialized) {
      window.addEventListener('scroll', maybeLoadMore, { passive: true });
      isInitialized = true;
    }

    maybeLoadMore();
  }

  function scheduleRefresh(force = false, delay = 250) {
    clearTimeout(refreshTimeout);
    refreshTimeout = setTimeout(() => {
      setupInfiniteScroll(force);
    }, delay);
  }

  function watchCollectionGridChanges() {
    if (mutationObserver) mutationObserver.disconnect();

    mutationObserver = new MutationObserver(() => {
      scheduleRefresh(false, 200);
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true
    });
  }

  function isFilterInteraction(target) {
    if (!target) return false;

    return !!target.closest(
      '.active-facets__button, .active-facets__button-wrapper, .facets__reset, .facets__disclosure, facet-remove, .facet-checkbox, .facets-container, .facet-filters, .sorting'
    );
  }

  document.addEventListener('DOMContentLoaded', () => {
    setupInfiniteScroll(true);
    watchCollectionGridChanges();
  });

  window.addEventListener('pageshow', () => {
    scheduleRefresh(true, 100);
  });

  window.addEventListener('popstate', () => {
    scheduleRefresh(true, 300);
  });

  document.addEventListener('shopify:section:load', () => {
    scheduleRefresh(true, 150);
  });

  document.addEventListener('click', (event) => {
    if (isFilterInteraction(event.target)) {
      scheduleRefresh(true, 350);
    }
  });

  document.addEventListener('change', (event) => {
    if (isFilterInteraction(event.target)) {
      scheduleRefresh(true, 350);
    }
  });
})();